import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ChooseFood from "assets/illustrations/chooseFoodImage.svg";
import OrderStep2Arrow from "assets/OrderStep2Arrow.svg";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerSubHeaderText } from "components/primitives/DrawerSubHeaderText";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { HeaderText } from "components/primitives/HeaderText";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useOpenDoorAlarm } from "hooks/useOpenDoorAlarm";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { selectActiveLocalTransaction } from "slices/transactionSlice";
import { OpenedDoorWarningBox } from "../components/OpenedDoorWarningBox";
import type { BaseOrderStepProps } from "../OrderStep2";

/**
 * Non-RFID prototype, door open: guidance instead of the live taken-meals list (there are no inventory
 * frames on this fridge type). The door-left-open warning and alarms behave exactly as on OrderStep3.
 */
export const NonRfidTakeStep = ({ open, toggleDrawer }: BaseOrderStepProps) => {
	const { t } = useTranslation();
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const { resetCountdown, displayOpenedDoorWarning } = useOpenDoorAlarm(transaction?.type, transaction?.state);

	useEffect(() => {
		if (displayOpenedDoorWarning) {
			toggleDrawer(true);
		}
	}, [displayOpenedDoorWarning, toggleDrawer]);

	return (
		<>
			<SwipeableArea orderstep={3} open={open}>
				{!open && <BlurredBackground orderStep={3} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Box sx={{ width: "100%", display: "flex", flexDirection: "column", paddingTop: 2 }}>
					<HeaderText variant="h3">{t("order.nonRfid.step3.header")}</HeaderText>
					<OrderStep2Arrow />
					<DrawerSubHeaderText>{t("order.nonRfid.step3.subheader")}</DrawerSubHeaderText>
				</Box>
			</SwipeableArea>
			<Body sx={{ ...(open && { marginTop: "220px" }) }}>
				<Hint>
					<PhoneIphoneOutlinedIcon sx={{ fontSize: 44 }} />
					<Typography variant="ctaSubtext">{t("order.nonRfid.step3.hint")}</Typography>
				</Hint>
				<ChooseFood />
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</Body>
		</>
	);
};

const Body = styled(Box)(() => ({
	width: "100%",
	maxHeight: "1000px",
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	padding: "0 0 120px 0",
	// The illustration is capped so the hint above it and the action bar below never collide.
	"& > svg": {
		height: "460px",
		width: "auto",
		marginTop: 24,
	},
}));

const Hint = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	gap: theme.spacing(2),
	marginTop: theme.spacing(1),
	color: theme.palette.common.white,
}));
